import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  SearchBox,
  Configure,
  InstantSearch,
  RefinementList,
  connectStateResults,
  QueryRuleCustomData
} from 'react-instantsearch-dom';
import { useSelector, useStore } from 'react-redux';

import { useRouter } from 'next/router';

import Hits from './results-list';
import MobileTrigger from './left-rail-mobile-trigger';
import NoResults from './results-none';
import PageHeader from '../../tissues/page-header';
import Refinements from './left-rail';
import ResultsHeader from './results-header';

import { Content } from '../../theme/page';
import PLPMeta from '../../utils/meta';
import getPLPMetadata from './utils/plpMetadata';
import { logAmplitude } from '../../utils/amplitude';
import checkRunningPromotion from '../../tissues/page-filter-header/utils/checkRunningPromotion';
import editsExceptions from '../../tissues/page-filter-header/utils/editsExceptions.json';

const PLP = connectStateResults(({
  edit,
  brand,
  trend,
  isOutlet,
  searchResults,
  searchState,
  isSearchStalled,
  meta
}) => {
  const pageNo = searchResults?.page;
  const hitsPP = searchResults?.hitsPerPage;
  const nbHits = searchResults?.nbHits;
  const totalPages = searchResults?.nbPages;
  const hasResultsAndNotOutOfBounds = searchResults?.nbHits !== 0
    || pageNo <= totalPages;
  const hasResults = hasResultsAndNotOutOfBounds && totalPages !== 0;
  const router = useRouter();

  useEffect(() => {
    // tracks Amplitude page view events
    logAmplitude(null, { nbHits, query: searchResults.query });
  }, [router?.asPath]);

  return (
    <main id="maincontent">
      <MobileTrigger
        isBrandRefined={!!brand}
        isShop={!edit && !brand && !trend}
        sortBy={searchState?.sortBy}
        css={{ display: !hasResults ? 'none' : undefined }}
        isOutlet={isOutlet}
      />

      <ResultsHeader edit={edit} brand={brand} trend={trend} isOutlet={isOutlet} />

      <Content css={{
        display: hasResults ? 'flex' : 'none',
        alignItems: 'flex-start',
        padding: '0rem'
      }}
      >
        <Refinements isBrandRefined={!!brand} edits={edit} isShop={!edit && !brand && !trend} isOutlet={isOutlet} />
        <Hits
          currentPage={pageNo ? +pageNo + 1 : 1}
          hitsPerPage={hitsPP}
          currentQuery={searchState?.query ?? ''}
          currentHierarchicalMenu={searchState?.hierarchicalMenu?.['categories_slug.lvl0'] ?? ''}
          currentRefinements={searchState?.refinementList ?? {}}
          currentSort={searchState?.sortBy ?? ''}
          isSearchStalled={isSearchStalled}
          sponsoredProducts={searchResults?.userData?.[0]?.sponsoredProducts || []}
          meta={meta}
        />

      </Content>

      <Content css={{ display: hasResults ? 'none' : undefined }}>
        <NoResults
          searchState={searchState}
          searchResults={searchResults}
          trend={trend}
          edit={edit}
        />
      </Content>

    </main>
  );
});

PLP.defaultProps = {
  brand: null,
  edit: '',
  trend: '',
  isOutlet: false,
  meta: {}
};

PLP.propTypes = {
  brand: PropTypes.object,
  edit: PropTypes.string,
  trend: PropTypes.string,
  isOutlet: PropTypes.bool,
  meta: PropTypes.object
};

const Algolia = (props) => {
  const { active_mini, minis } = useSelector((state) => state.petites);
  const [miniFilters, setMiniFilters] = useState({});
  const store = useStore();

  useEffect(() => {
    if (active_mini && minis.length) {
      const mini = minis.find((x) => x.id === active_mini);

      if (mini) {
        const refinements = {};

        const { gender_taxons, age_range_taxons } = mini;

        if (gender_taxons) refinements.gender = gender_taxons.map((x) => x.name);
        if (age_range_taxons) refinements['variants.age_range'] = age_range_taxons.map((x) => x.name);

        setMiniFilters(refinements);
      }
    } else setMiniFilters({});
  }, [active_mini, minis]);

  return (
    <>
      <PLPMeta
        trend={props.trend}
        brand={props.brand}
        edit={props.edit}
        {...props}
      />

      <InstantSearch
        searchClient={props.searchClient}
        resultsState={props.resultsState}
        onSearchStateChange={props.onSearchStateChange}
        searchState={props.searchState}
        createURL={props.createURL}
        indexName={props.indexName}
        {...props}
      >

        {/* This is to enable Algolia personlization for logged in user */}
        <Configure
          enablePersonalization={!!store.getState().profile?.id || false}
          userToken={store.getState().profile?.id}
          hitsPerPage={24}
          clickAnalytics
        />

        <span css={{ display: 'none' }}>
          {/* this RefinementList is necessary for filtered URLs */}
          <RefinementList attribute="trends" />
          {/* need this RefinementList for the URL path /trends/ */}
          {props.trend && <RefinementList attribute="trends_slug" defaultRefinement={[props.trend]} />}
          {props.edit && <RefinementList attribute="edits" defaultRefinement={[props.edit]} />}
          {props.brand && <RefinementList attribute="brand" defaultRefinement={[props.brand.name ?? null]} />}
          {miniFilters.gender && <RefinementList attribute="gender" defaultRefinement={miniFilters.gender} />}
          {miniFilters['variants.age_range'] && <RefinementList attribute="variants.age_range" defaultRefinement={miniFilters['variants.age_range']} />}
        </span>

        <SearchBox css={{ display: 'none' }} />

        <QueryRuleCustomData>
          {({ items }) => {
            if (props.edit) {
              const livePromotion = (checkRunningPromotion(Date.now())
                && editsExceptions[props.edit]);

              const { heading } = getPLPMetadata('edit', props);
              const [item] = items;
              // item.name is set in Algolia as a rule and is not always present.
              // use hardcoded override heading in getPLPMetadata(), if available, for SEO purposes.
              if (heading && !livePromotion) return <PageHeader key={heading} heading={heading} />;
              if (item?.name) return <PageHeader key={item.name} heading={item.name} />;
            }

            return null;
          }}
        </QueryRuleCustomData>

        <PLP
          brand={props.brand}
          edit={props.edit}
          trend={props.trend}
          isOutlet={props.isOutlet}
          meta={props.meta}
        />
      </InstantSearch>
    </>
  );
};

Algolia.defaultProps = {
  brand: null,
  edit: null,
  children: null,
  miniFilters: {},
  trend: null,
  createURL: () => { },
  isOutlet: false,
  meta: {}
};

Algolia.propTypes = {
  brand: PropTypes.object,
  children: PropTypes.node,
  createURL: PropTypes.func,
  edit: PropTypes.string,
  miniFilters: PropTypes.object,
  indexName: PropTypes.string.isRequired,
  onSearchStateChange: PropTypes.func.isRequired,
  resultsState: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  searchClient: PropTypes.object.isRequired,
  searchState: PropTypes.object.isRequired,
  trend: PropTypes.string,
  isOutlet: PropTypes.bool,
  meta: PropTypes.object
};

export default Algolia;
